// API bindings: admin_data_space (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl } from "./utils";
import { AdminDataSpaceStatus, AdminDataSpaceFeatures, CheckDataSpaceConfigurationResponse } from "./definitions";

export class ApiAdminDataSpace {
    /**
     * Method: GET
     * Path: /admin/ds
     * Gets current status of the special features of the connector
     * @returns The request parameters
     */
    public static GetFeatures(): RequestParams<AdminDataSpaceStatus, CommonAuthenticatedErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/ds`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/features
     * Sets current status of the special features of the connector
     * Requires permission: admin.ds
     * @param body Body parameters
     * @returns The request parameters
     */
    public static SetFeatures(body: AdminDataSpaceFeatures): RequestParams<void, SetFeaturesErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/features`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /admin/ds/check
     * Checks data space configuration
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static CheckDataSpaceConfiguration(): RequestParams<CheckDataSpaceConfigurationResponse, CheckDataSpaceConfigurationErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/admin/ds/check`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/onboarding/dismiss
     * Retires the onboarding wizard, either because the admin completed it or
     * because they exited it half way. It is a one-shot first-run flow, so it is
     * never shown again afterwards: the missing-configuration banner is what
     * points at whatever is still missing from then on.
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static DismissOnboarding(): RequestParams<void, DismissOnboardingErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/onboarding/dismiss`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/skip/ca
     * Skips certification authorities configuration step
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static SkipCertificationAuthorities(): RequestParams<void, SkipCertificationAuthoritiesErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/skip/ca`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/skip/attestators
     * Skips DID attestators configuration step
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static SkipDidAttestators(): RequestParams<void, SkipDidAttestatorsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/skip/attestators`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/skip/brokers
     * Skips catalog metadata brokers
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static SkipCatalogMetadataBrokers(): RequestParams<void, SkipCatalogMetadataBrokersErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/skip/brokers`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/skip/marketplaces
     * Skips app marketplaces
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static SkipAppMarketplaces(): RequestParams<void, SkipAppMarketplacesErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/skip/marketplaces`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/skip/broker_request
     * Skips catalog metadata broker request
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static SkipCatalogMetadataBrokerRequest(): RequestParams<void, SkipCatalogMetadataBrokerRequestErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/skip/broker_request`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /admin/ds/skip/clearing_house
     * Skips catalog metadata broker request
     * Requires permission: admin.ds
     * @returns The request parameters
     */
    public static SkipClearingHouseConfig(): RequestParams<void, SkipClearingHouseConfigErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/admin/ds/skip/clearing_house`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for SetFeatures
 */
export type SetFeaturesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for CheckDataSpaceConfiguration
 */
export type CheckDataSpaceConfigurationErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for DismissOnboarding
 */
export type DismissOnboardingErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SkipCertificationAuthorities
 */
export type SkipCertificationAuthoritiesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SkipDidAttestators
 */
export type SkipDidAttestatorsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SkipCatalogMetadataBrokers
 */
export type SkipCatalogMetadataBrokersErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SkipAppMarketplaces
 */
export type SkipAppMarketplacesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SkipCatalogMetadataBrokerRequest
 */
export type SkipCatalogMetadataBrokerRequestErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SkipClearingHouseConfig
 */
export type SkipClearingHouseConfigErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

